// Infinote box builder: cart enhancement.
// Groups box lines (linked by the hidden _box_id property) into one box card in the cart page and drawer,
// with one boxes stepper, Edit box and Remove, and adds a recipient-list (CSV) upload on the cart page.
// Works by tagging rows with data-ibb-key (see snippets/cart-item.liquid, sections/main-cart-items.liquid)
// and re-grouping whenever the theme re-renders cart HTML.
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // Wording lives in the theme locale (group "infinote_box") and arrives as window.infinoteBox.copy.
  // tx: plain text with {placeholders} filled. t: the same, HTML-escaped (for markup).
  const copy = () => (window.infinoteBox && window.infinoteBox.copy) || {};
  const tx = (key, vars) => String(copy()[key] == null ? key : copy()[key]).replace(/\{(\w+)\}/g, (m, k) => (vars && k in vars ? vars[k] : m));
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const t = (key, vars) => { const safe = {}; Object.keys(vars || {}).forEach((k) => { safe[k] = esc(vars[k]); }); return esc(String(copy()[key] == null ? key : copy()[key])).replace(/\{(\w+)\}/g, (m, k) => (k in safe ? safe[k] : m)); };
  const getCart = () => fetch(root + 'cart.js').then((r) => r.json());
  const post = (path, body) => fetch(root + path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  // Same look as the theme's prices ("Rs. 1,249.00") so a box row and a normal row read alike.
  const money = (cart) => ({
    format: (v) => (!cart.currency || cart.currency === 'INR'
      ? 'Rs. ' + v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cart.currency }).format(v)),
  });

  // ---------- grouping ----------
  let running = false, again = false;
  async function group() {
    if (running) { again = true; return; }
    const rows = [...document.querySelectorAll('[data-ibb-key]')].filter((r) => !r.dataset.ibbDone);
    if (!rows.length) return;
    running = true;
    try {
      const cart = await getCart();
      const byKey = new Map(cart.items.map((i) => [i.key, i]));
      const groups = {};
      cart.items.forEach((i) => { const id = i.properties && i.properties._box_id; if (id) (groups[id] = groups[id] || []).push(i); });
      rows.forEach((row) => {
        row.dataset.ibbDone = '1';
        const item = byKey.get(row.dataset.ibbKey);
        const id = item && item.properties && item.properties._box_id;
        if (!id) return;
        if (item.properties._box_role === 'item') { row.classList.add('ibb-cart-hidden'); return; }
        renderBox(row, item, groups[id], cart);
      });
      renderRecipients(cart, Object.keys(groups).length > 0);
    } finally { running = false; if (again) { again = false; setTimeout(group, 80); } }
  }

  function renderBox(row, box, lines, cart) {
    const fmt = money(cart);
    const items = lines.filter((l) => l.properties._box_role === 'item');
    const n = box.quantity;
    const total = lines.reduce((s, l) => s + l.final_line_price, 0);
    const orig = lines.reduce((s, l) => s + l.original_line_price, 0);
    const p = box.properties;
    // Per-box breakdown: the box, then each item with its price, so the total is explainable.
    const line = (name, price) => `<li><span>${name}</span><span>${fmt.format(price / 100)}</span></li>`;
    const list = line(t('box_line'), box.original_price)
      + items.map((l) => line(`${esc(l.product_title)} × ${Math.round(l.quantity / n)}`, l.original_price * Math.round(l.quantity / n))).join('');
    const disc = {};
    lines.forEach((l) => (l.discounts || []).forEach((d) => { disc[d.title] = (disc[d.title] || 0) + d.amount; }));
    const discHtml = Object.entries(disc).map(([t, a]) => `<li><span>${esc(t)}</span><span>−${fmt.format(a / 100)}</span></li>`).join('');
    const noteKeys = { 'Gift to': 'gift_to', 'Gift from': 'gift_from', 'Gift message': 'gift_message' };
    const note = Object.keys(noteKeys).filter((k) => p[k]).map((k) => `<li>${t(noteKeys[k])}: ${esc(p[k])}</li>`).join('');
    const custom = p._custom_dims_mm ? `<p class="ibb-cartbox__muted">${t('cart_made_to_size', { dims: p._custom_dims_mm.replace(/x/g, ' × ') + ' mm' })}</p>` : '';
    const editUrl = ((window.infinoteBox && window.infinoteBox.builderUrl) || '/pages/build-a-box') + '?edit=' + encodeURIComponent(p._box_id);
    const img = box.image ? `<img src="${esc(box.image)}" alt="" width="96" height="96" loading="lazy">`
      : `<span class="ibb-cartbox__ph" aria-hidden="true">${esc((box.product_title || '?').trim().charAt(0))}</span>`;
    const details = `<p class="ibb-cartbox__title">${esc(box.product_title)}</p>${custom}
      <ul class="ibb-cartbox__list">${list}</ul>
      ${discHtml ? `<ul class="ibb-cartbox__list ibb-cartbox__disc">${discHtml}</ul>` : ''}
      ${note ? `<ul class="ibb-cartbox__list ibb-cartbox__muted">${note}</ul>` : ''}`;
    const stepper = `<span class="ibb-cartbox__count"><button type="button" data-ibb-qty="-1" aria-label="${t('fewer_boxes')}">−</button><span aria-live="polite">${n}</span><button type="button" data-ibb-qty="1" aria-label="${t('more_boxes')}">+</button></span>
      <span class="ibb-cartbox__links"><a class="ibb-cartbox__link" href="${esc(editUrl)}">${t('cart_edit')}</a><button type="button" class="ibb-cartbox__link" data-ibb-remove>${t('cart_remove')}</button></span>`;
    const perBox = fmt.format(orig / n / 100);
    const totalHtml = `${orig > total ? `<s class="ibb-cartbox__was">${fmt.format(orig / 100)}</s>` : ''}<strong>${fmt.format(total / 100)}</strong>`;
    if (row.tagName === 'TR') {
      row.classList.add('ibb-cartrow');
      row.innerHTML = `<td class="ibb-cartrow__img">${img}</td><td class="ibb-cartrow__body" colspan="1">${details}</td>
        <td class="ibb-cartrow__price"><span class="ibb-cartrow__label">${t('cart_per_box')}</span>${perBox}</td>
        <td class="ibb-cartrow__qty">${stepper}</td><td class="ibb-cartrow__total">${totalHtml}</td>`;
    } else { // cart drawer: compact card
      row.innerHTML = `<div class="ibb-cartbox"><div class="ibb-cartbox__thumbs">${img}</div><div class="ibb-cartbox__body">${details}
        <div class="ibb-cartbox__actions">${stepper}</div></div><div class="ibb-cartbox__price">${totalHtml}</div></div>`;
    }
    row.dataset.ibbBox = p._box_id;
    row.querySelector('[data-ibb-remove]').addEventListener('click', () => setGroup(row, cart, lines, box, 0));
    const minus = row.querySelector('[data-ibb-qty="-1"]');
    if (n <= 1) { minus.disabled = true; minus.setAttribute('aria-disabled', 'true'); } // removing is the Remove button's job
    row.querySelectorAll('[data-ibb-qty]').forEach((b) => b.addEventListener('click', () => setGroup(row, cart, lines, box, n + Number(b.dataset.ibbQty))));
  }

  // Scale every line of the box together (items keep their per-box quantity).
  async function setGroup(row, cart, lines, box, n) {
    if (row.dataset.ibbBusy) return; // ignore taps while an update is in flight
    row.dataset.ibbBusy = '1';
    row.querySelectorAll('button').forEach((b) => { b.disabled = true; });
    const keys = new Set(lines.map((l) => l.key));
    const updates = cart.items.map((i) => (keys.has(i.key) ? (i.properties._box_role === 'box' ? n : Math.round(i.quantity / box.quantity) * n) : i.quantity));
    let msg = '';
    try {
      const res = await post('cart/update.js', { updates });
      if (res.ok) { location.reload(); return; }
      msg = (await res.json()).description || tx('cart_update_failed');
    } catch (e) { msg = tx('err_network'); }
    // Failed (for example not enough stock): say so and give the controls back.
    delete row.dataset.ibbBusy;
    row.querySelectorAll('button').forEach((b) => { b.disabled = b.getAttribute('aria-disabled') === 'true'; });
    let err = row.querySelector('.ibb-cartbox__error');
    if (!err) { err = document.createElement('p'); err.className = 'ibb-cartbox__error'; err.setAttribute('role', 'alert'); (row.querySelector('.ibb-cartbox__body, .ibb-cartrow__body')).appendChild(err); }
    err.textContent = msg;
  }

  // ---------- recipient list upload (CSV) ----------
  const COLUMNS = [['first_name', 'col_first_name', true], ['last_name', 'col_last_name', true], ['address', 'col_address', true], ['apartment', 'col_apartment', false],
    ['company', 'col_company', false], ['city', 'col_city', true], ['country', 'col_country', true], ['state', 'col_state', true], ['zip', 'col_zip', true]];
  const colLabel = (c) => tx(c[1]);

  function parseCsv(text) {
    const first = text.split(/\r?\n/, 1)[0] || '';
    const sep = !first.includes(',') && first.includes(';') ? ';' : !first.includes(',') && first.includes('\t') ? '\t' : ','; // Excel in some locales saves with ; or tab
    const rows = []; let row = [], cur = '', q = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (q) { if (c === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += c; }
      else if (c === '"') q = true;
      else if (c === sep) { row.push(cur); cur = ''; }
      else if (c === '\n' || c === '\r') { if (c === '\r' && text[i + 1] === '\n') i++; row.push(cur); rows.push(row); row = []; cur = ''; }
      else cur += c;
    }
    if (cur !== '' || row.length) { row.push(cur); rows.push(row); }
    return rows.filter((r) => r.some((v) => v.trim() !== ''));
  }

  // ---------- minimal .xlsx reader (zip + XML, no library) ----------
  async function inflateRaw(bytes) {
    const ds = new DecompressionStream('deflate-raw');
    const out = new Blob([bytes]).stream().pipeThrough(ds);
    return new Uint8Array(await new Response(out).arrayBuffer());
  }
  async function unzip(buf) {
    const v = new DataView(buf), u8 = new Uint8Array(buf);
    let eocd = -1;
    for (let i = buf.byteLength - 22; i >= Math.max(0, buf.byteLength - 70000); i--) if (v.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
    if (eocd < 0) throw new Error(tx('rec_err_xlsx'));
    const count = v.getUint16(eocd + 10, true);
    let p = v.getUint32(eocd + 16, true);
    const files = {}, dec = new TextDecoder();
    for (let n = 0; n < count; n++) {
      if (v.getUint32(p, true) !== 0x02014b50) break;
      const method = v.getUint16(p + 10, true), csize = v.getUint32(p + 20, true);
      const nl = v.getUint16(p + 28, true), el = v.getUint16(p + 30, true), cl = v.getUint16(p + 32, true), off = v.getUint32(p + 42, true);
      const name = dec.decode(u8.subarray(p + 46, p + 46 + nl));
      const lnl = v.getUint16(off + 26, true), lel = v.getUint16(off + 28, true);
      const data = u8.subarray(off + 30 + lnl + lel, off + 30 + lnl + lel + csize);
      files[name] = { method, data };
      p += 46 + nl + el + cl;
    }
    const read = async (name) => { const f = files[name]; if (!f) return null; return dec.decode(f.method === 0 ? f.data : await inflateRaw(f.data)); };
    return { read, names: Object.keys(files) };
  }
  const colIndex = (ref) => { const m = /^([A-Z]+)/.exec(ref); return [...m[1]].reduce((n, c) => n * 26 + c.charCodeAt(0) - 64, 0) - 1; };
  async function parseXlsx(file) {
    if (typeof DecompressionStream === 'undefined') throw new Error(tx('rec_err_browser'));
    const z = await unzip(await file.arrayBuffer());
    const parser = new DOMParser();
    const shared = [];
    const ss = await z.read('xl/sharedStrings.xml');
    if (ss) parser.parseFromString(ss, 'application/xml').querySelectorAll('si').forEach((si) => { shared.push([...si.querySelectorAll('t')].map((t) => t.textContent).join('')); });
    const sheetName = z.names.filter((n) => /^xl\/worksheets\/sheet\d+\.xml$/.test(n)).sort()[0];
    if (!sheetName) throw new Error(tx('rec_err_sheet'));
    const doc = parser.parseFromString(await z.read(sheetName), 'application/xml');
    const rows = [];
    doc.querySelectorAll('sheetData > row').forEach((r) => {
      const row = [];
      r.querySelectorAll('c').forEach((c) => {
        const i = colIndex(c.getAttribute('r') || 'A1'), t = c.getAttribute('t');
        let val = '';
        if (t === 's') val = shared[Number(c.querySelector('v')?.textContent)] || '';
        else if (t === 'inlineStr') val = [...c.querySelectorAll('t')].map((x) => x.textContent).join('');
        else val = c.querySelector('v')?.textContent || '';
        row[i] = val;
      });
      rows.push(Array.from(row, (x) => (x == null ? '' : String(x))));
    });
    return rows.filter((r) => r.some((v) => v.trim() !== ''));
  }

  const norm = (h) => h.toLowerCase().replace(/[^a-z]/g, '');
  const aliases = { firstname: 'first_name', lastname: 'last_name', address: 'address', apartments: 'apartment', apartment: 'apartment', company: 'company', city: 'city', country: 'country', state: 'state', zipcode: 'zip', zip: 'zip', postalcode: 'zip', pincode: 'zip' };

  function validate(rows) {
    const errors = [];
    if (!rows.length) return { errors: [tx('rec_empty')], recipients: [] };
    const idx = {};
    rows[0].forEach((h, i) => { const k = aliases[norm(h)]; if (k && !(k in idx)) idx[k] = i; });
    COLUMNS.filter((c) => c[2] && !(c[0] in idx)).forEach((c) => errors.push(tx('rec_missing_col', { col: colLabel(c) })));
    if (errors.length) return { errors, recipients: [] };
    const recipients = [];
    rows.slice(1).forEach((r, n) => {
      const rec = {}; COLUMNS.forEach(([k]) => { rec[k] = k in idx ? (r[idx[k]] || '').trim() : ''; });
      const miss = COLUMNS.filter((c) => c[2] && !rec[c[0]]).map(colLabel);
      if (miss.length) errors.push(tx('rec_missing_row', { row: n + 2, cols: miss.join(', ') })); else recipients.push(rec);
    });
    return { errors, recipients };
  }

  const toCsv = (recs) => [COLUMNS.map(colLabel).join(',')].concat(recs.map((r) => COLUMNS.map(([k]) => `"${(r[k] || '').replace(/"/g, '""')}"`).join(','))).join('\n');

  function renderRecipients(cart, hasBoxes) {
    const anchor = document.getElementById('main-cart-items');
    const existing = document.getElementById('ibb-recipients');
    if (!anchor || !hasBoxes) { if (existing && !hasBoxes) existing.remove(); return; }
    if (existing) return;
    const boxes = cart.items.filter((i) => i.properties && i.properties._box_role === 'box').reduce((s, i) => s + i.quantity, 0);
    let saved = (cart.attributes && cart.attributes['Recipients']) || '';
    const panel = document.createElement('section');
    panel.id = 'ibb-recipients'; panel.className = 'ibb-recipients';
    const boxesText = tx(boxes === 1 ? 'box_one' : 'box_many', { n: boxes });
    panel.innerHTML = `<h2 class="ibb-recipients__title">${t('rec_title')}</h2>
      <p class="ibb-cartbox__muted">${t('rec_intro', { boxes: boxesText })}</p>
      <label class="ibb-drop" data-ibb-drop><input type="file" class="ibb-drop__input" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" data-ibb-file>
        <span class="ibb-drop__main">${t('drop_main')}</span><span class="ibb-drop__sub">${t('drop_sub')}</span></label>
      <p class="ibb-recipients__row"><button type="button" class="ibb-cartbox__link" data-ibb-template>${t('rec_template')}</button></p>
      <div class="ibb-recipients__result" role="status" aria-live="polite"></div>`;
    // Inside the items column, under the cart table (falls back to after the items container)
    (anchor.querySelector('table') || anchor).insertAdjacentElement('afterend', panel);
    const result = panel.querySelector('.ibb-recipients__result');
    // Paint a message plus the currently saved list (with its remove control), so the saved state is always visible.
    const paint = (html) => {
      result.innerHTML = `${html || ''}${saved ? `<p>${t('rec_saved', { summary: saved })} <button type="button" class="ibb-cartbox__link" data-ibb-clear>${t('rec_remove')}</button></p>` : ''}`;
      const b = result.querySelector('[data-ibb-clear]');
      if (b) b.addEventListener('click', async () => { await post('cart/update.js', { attributes: { Recipients: '', 'Recipients data': '' } }); saved = ''; paint(t('rec_removed')); });
    };
    paint('');
    panel.querySelector('[data-ibb-template]').addEventListener('click', () => {
      const blob = new Blob([COLUMNS.map(colLabel).join(',') + '\n'], { type: 'text/csv' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'recipients-template.csv'; a.click(); URL.revokeObjectURL(a.href);
    });
    const input = panel.querySelector('[data-ibb-file]');
    const drop = panel.querySelector('[data-ibb-drop]');
    ['dragenter', 'dragover'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-over'); }));
    ['dragleave', 'drop'].forEach((ev) => drop.addEventListener(ev, () => drop.classList.remove('is-over')));
    drop.addEventListener('drop', (e) => { e.preventDefault(); if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); });
    input.addEventListener('change', () => { if (input.files[0]) handleFile(input.files[0]).then(() => { input.value = ''; }); });
    async function handleFile(f) {
      if (f.size > 5 * 1024 * 1024) { paint(t('rec_err_big')); return; }
      if (/\.xls$/i.test(f.name)) { paint(t('rec_err_xls')); return; }
      let rows;
      try { rows = /\.xlsx$/i.test(f.name) ? await parseXlsx(f) : parseCsv(await f.text()); }
      catch (err) { paint(esc(err.message || tx('rec_err_read'))); return; }
      const { errors, recipients } = validate(rows);
      if (errors.length) paint(`<strong>${esc(errors.length === 1 ? tx('rec_problems_one') : tx('rec_problems_many', { n: errors.length }))}</strong><ul>${errors.slice(0, 8).map((x) => `<li>${esc(x)}</li>`).join('')}</ul>${errors.length > 8 ? `<p>${t('rec_and_more', { n: errors.length - 8 })}</p>` : ''}`);
      else if (!recipients.length) paint(t('rec_none'));
      else {
        const summary = tx(recipients.length === 1 ? 'rec_uploaded_one' : 'rec_uploaded_many', { n: recipients.length });
        const res = await post('cart/update.js', { attributes: { Recipients: summary, 'Recipients data': toCsv(recipients) } });
        if (res.ok) { saved = summary; paint(recipients.length !== boxes ? t('rec_count_mismatch', { boxes: boxesText, n: recipients.length }) : ''); }
        else paint(t('rec_err_save'));
      }
    }
  }

  // ---------- header / drawer count: boxes, not lines ----------
  let countTimer;
  async function fixCount() {
    const cart = await getCart();
    const boxes = cart.items.filter((i) => i.properties && i.properties._box_role === 'box').reduce((s, i) => s + i.quantity, 0);
    const loose = cart.items.filter((i) => !(i.properties && i.properties._box_id)).reduce((s, i) => s + i.quantity, 0);
    const n = boxes + loose;
    if (boxes === 0) return; // nothing to override; leave the theme's count alone
    // Change only the digits inside existing text nodes so the theme's markup (hidden screen-reader text) stays intact.
    document.querySelectorAll('.cart-count').forEach((el) => {
      const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      for (let node = w.nextNode(); node; node = w.nextNode()) {
        const want = node.nodeValue.replace(/\d+/g, String(n));
        if (want !== node.nodeValue) node.nodeValue = want;
      }
    });
  }
  const scheduleCount = () => { clearTimeout(countTimer); countTimer = setTimeout(fixCount, 150); };

  // ---------- run now and on any cart re-render ----------
  let timer;
  const schedule = () => { clearTimeout(timer); timer = setTimeout(group, 80); scheduleCount(); };
  // Only react when cart rows or the cart count are (re)rendered; ignore unrelated DOM churn (timers, sliders, popups).
  const HOOKS = '[data-ibb-key], .cart-count';
  const relevant = (muts) => muts.some((m) => {
    const t = m.target.nodeType === 1 ? m.target : m.target.parentElement;
    if (t && t.closest && t.closest('.cart-count')) return true;
    return [...m.addedNodes].some((n) => n.nodeType === 1 && (n.matches(HOOKS) || n.querySelector(HOOKS)));
  });
  new MutationObserver((muts) => { if (relevant(muts)) schedule(); }).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', schedule);
  schedule();
})();
